import type { WorkbookSheet } from "../../../shared/account-finance/finance-workbook-contract";
import type { FinanceCashRecord } from "./finance-workbook-query";
import { workbookRowIncluded } from "./finance-workbook-presentation";

export type FinanceRecordFilters = {
  search: string; account: string; from: string; to: string; currency: string;
  direction: "all" | "incoming" | "outgoing" | "none";
  due: "all" | "overdue" | "upcoming" | "undated";
  inclusion: "all" | "included" | "excluded";
  sort: "date-asc" | "date-desc" | "amount-desc" | "amount-asc" | "name";
};
export const emptyFinanceFilters = (): FinanceRecordFilters => ({
  search: "", account: "", from: "", to: "", currency: "all", direction: "all",
  due: "all", inclusion: "all", sort: "date-asc",
});
const normalize = (value: string) => value.trim().toLocaleLowerCase("tr-TR");
const absolute = (value: bigint) => value < 0n ? -value : value;
const compare = (left: bigint, right: bigint) => left < right ? -1 : left > right ? 1 : 0;

export function financeSheetRecords(sheet: WorkbookSheet): FinanceCashRecord[] {
  const seen = new Set<string>();
  return sheet.rows.filter(row => {
    if (seen.has(row.id)) return false;
    seen.add(row.id); return true;
  }).map(row => ({ key: `${sheet.id}:${row.id}`, sheetId: sheet.id, movementId: sheet.id,
    row, amount: sheet.kind === "balance" || sheet.kind === "ledger" ? BigInt(row.remaining)
      : row.direction === "outgoing" ? -absolute(BigInt(row.remaining)) : BigInt(row.remaining),
    included: !sheet.hasMore && workbookRowIncluded(sheet, row),
  }));
}

export function filterFinanceRecords(records: FinanceCashRecord[], filters: FinanceRecordFilters, reportDate: string) {
  if (filters.from && filters.to && filters.from > filters.to) return [];
  const term = normalize(filters.search), account = normalize(filters.account);
  const until = new Date(`${reportDate}T00:00:00Z`); until.setUTCDate(until.getUTCDate() + 6);
  const next7End = until.toISOString().slice(0, 10);
  return records.filter(record => {
    const { row } = record;
    return (!term || normalize(`${row.code} ${row.name} ${row.document} ${row.status} ${row.date ?? ""}`).includes(term))
      && (!account || normalize(`${row.code} ${row.name}`).includes(account))
      && (!filters.from || (row.date !== null && row.date >= filters.from))
      && (!filters.to || (row.date !== null && row.date <= filters.to))
      && (filters.currency === "all" || row.currency === filters.currency)
      && (filters.direction === "all" || row.direction === filters.direction)
      && (filters.inclusion === "all" || (filters.inclusion === "included") === record.included)
      && (filters.due === "all" || (row.direction !== "none" && record.movementId !== "opening" && (filters.due === "undated" ? row.date === null
        : row.date !== null && (filters.due === "overdue" ? row.date < reportDate : row.date >= reportDate && row.date <= next7End))));
  }).sort((left, right) => {
    let result: number;
    if (filters.sort === "name") result = left.row.name.localeCompare(right.row.name, "tr");
    else if (filters.sort.startsWith("amount")) {
      result = left.row.currency.localeCompare(right.row.currency, "tr")
        || compare(absolute(left.amount), absolute(right.amount)) * (filters.sort === "amount-desc" ? -1 : 1);
    } else {
      if (left.row.date === null || right.row.date === null) result = Number(left.row.date === null) - Number(right.row.date === null);
      else result = left.row.date.localeCompare(right.row.date) * (filters.sort === "date-desc" ? -1 : 1);
    }
    return result || left.key.localeCompare(right.key);
  });
}

export function financeRecordTotals(records: FinanceCashRecord[]) {
  const currencies = new Map<string, { currency: string; incoming: bigint; outgoing: bigint; total: bigint }>();
  for (const record of records) {
    const entry = currencies.get(record.row.currency) ?? { currency: record.row.currency, incoming: 0n, outgoing: 0n, total: 0n };
    entry.total += record.amount;
    if (record.row.direction === "incoming") entry.incoming += absolute(record.amount);
    if (record.row.direction === "outgoing") entry.outgoing += absolute(record.amount);
    currencies.set(entry.currency, entry);
  }
  return [...currencies.values()].sort((left, right) => left.currency.localeCompare(right.currency));
}
